import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import { dramas } from '@/mocks/dramas';
import { actors } from '@/mocks/actors';
import { reviews } from '@/mocks/reviews';

const typeFilters = [
  { key: 'all', label: '全部' },
  { key: 'drama', label: '短剧' },
  { key: 'actor', label: '演员' },
  { key: 'review', label: '影评' },
];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const [input, setInput] = useState(q);
  const [type, setType] = useState('all');

  useEffect(() => {
    setInput(q);
  }, [q]);

  const keyword = q.trim().toLowerCase();

  const matchedDramas = useMemo(() => {
    if (!keyword) return [];
    return dramas.filter((d) =>
      [d.title, d.synopsis, d.director, d.region, ...d.genres, ...d.tags]
        .join(' ')
        .toLowerCase()
        .includes(keyword),
    );
  }, [keyword]);

  const matchedActors = useMemo(() => {
    if (!keyword) return [];
    return actors.filter((a) => [a.name, a.bio, a.region, ...a.tags].join(' ').toLowerCase().includes(keyword));
  }, [keyword]);

  const matchedReviews = useMemo(() => {
    if (!keyword) return [];
    return reviews.filter((r) => [r.title, r.excerpt, r.author, ...r.tags].join(' ').toLowerCase().includes(keyword));
  }, [keyword]);

  const showDrama = type === 'all' || type === 'drama';
  const showActor = type === 'all' || type === 'actor';
  const showReview = type === 'all' || type === 'review';

  const totalCount =
    (showDrama ? matchedDramas.length : 0) +
    (showActor ? matchedActors.length : 0) +
    (showReview ? matchedReviews.length : 0);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed) return;
    setSearchParams({ q: trimmed });
  };

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-8 py-8 md:py-12">
        <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-5">
          <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-300">搜索结果</span>
        </nav>

        <h1 className="text-2xl md:text-3xl font-bold text-foreground-50 font-heading mb-5">搜索中心</h1>

        <form onSubmit={submit} className="max-w-2xl">
          <div className="relative">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-500 text-lg" />
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入短剧名称、演员姓名或影评关键词…"
              className="w-full rounded-lg bg-background-900 border border-background-700 py-3.5 pl-12 pr-28 text-sm text-foreground-100 placeholder:text-foreground-600 focus:border-primary-500 focus:outline-none"
            />
            <button
              type="submit"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md bg-primary-600 px-5 py-2 text-sm font-medium text-background-50 hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </div>
        </form>

        {q && (
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <span className="text-sm text-foreground-400">
              关键词 <span className="text-primary-400 font-medium">“{q}”</span> · 共 {totalCount} 条结果
            </span>
            <div className="inline-flex items-center gap-1 rounded-full bg-background-900 p-1">
              {typeFilters.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setType(f.key)}
                  className={`rounded-full px-3.5 py-1.5 text-xs transition-colors cursor-pointer whitespace-nowrap ${
                    type === f.key ? 'bg-primary-600 text-background-50 font-medium' : 'text-foreground-300 hover:text-foreground-50'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {!q ? (
          <div className="mt-12 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-700 bg-background-900/40 py-20">
            <i className="ri-search-2-line text-5xl text-foreground-600" />
            <p className="mt-4 text-foreground-300">输入关键词开始搜索</p>
            <p className="mt-1.5 text-sm text-foreground-500">支持搜索短剧标题、演员与影评内容</p>
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {['都市', '悬疑', '爱情', '林澈'].map((kw) => (
                <button
                  key={kw}
                  type="button"
                  onClick={() => setSearchParams({ q: kw })}
                  className="rounded-full bg-background-800 px-4 py-1.5 text-xs text-foreground-300 hover:bg-background-700 transition-colors cursor-pointer"
                >
                  {kw}
                </button>
              ))}
            </div>
          </div>
        ) : totalCount === 0 ? (
          <div className="mt-12 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-700 bg-background-900/40 py-20">
            <i className="ri-emotion-sad-line text-5xl text-foreground-600" />
            <p className="mt-4 text-foreground-300">没有找到与“{q}”匹配的内容</p>
            <p className="mt-1.5 text-sm text-foreground-500">试试更换关键词，或浏览分类页发现更多作品</p>
            <Link to="/" className="mt-6 rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap">
              返回首页
            </Link>
          </div>
        ) : (
          <div className="mt-10 flex flex-col gap-10">
            {showDrama && matchedDramas.length > 0 && (
              <section>
                <h2 className="mb-4 text-sm font-semibold text-foreground-400">
                  相关短剧（{matchedDramas.length}）
                </h2>
                <div className="flex flex-col gap-3">
                  {matchedDramas.map((d) => (
                    <Link
                      key={d.id}
                      to={`/detail/${d.id}`}
                      className="group flex gap-4 rounded-lg border border-background-800 bg-background-900/50 p-3 hover:border-primary-700/70 transition-colors cursor-pointer"
                    >
                      <div className="h-20 w-14 shrink-0 overflow-hidden rounded-md bg-background-800">
                        <img src={d.poster} alt={`${d.title} 海报`} title={`${d.title} - 成人短剧详情`} className="w-full h-full object-cover object-top" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-semibold text-foreground-50 group-hover:text-primary-300 transition-colors">
                          {d.title}
                        </h3>
                        <p className="mt-1 text-xs text-foreground-500">
                          {d.year} · {d.region} · {d.genres.join(' / ')} · 评分 {d.rating.toFixed(1)}
                        </p>
                        <p className="mt-1.5 text-xs text-foreground-400 line-clamp-2">{d.synopsis}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {showActor && matchedActors.length > 0 && (
              <section>
                <h2 className="mb-4 text-sm font-semibold text-foreground-400">
                  相关演员（{matchedActors.length}）
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {matchedActors.map((a) => (
                    <Link
                      key={a.id}
                      to={`/actor/${a.id}`}
                      className="group flex items-center gap-3 rounded-lg border border-background-800 bg-background-900/50 p-3 hover:border-primary-700/70 transition-colors cursor-pointer"
                    >
                      <div className="h-14 w-12 shrink-0 overflow-hidden rounded-md bg-background-800">
                        <img src={a.avatar} alt={`${a.name} 演员照`} title={`${a.name} - 成人短剧演员资料`} className="w-full h-full object-cover object-top" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm font-semibold text-foreground-50 group-hover:text-primary-300 transition-colors">{a.name}</h3>
                        <p className="mt-0.5 text-xs text-foreground-500 truncate">{a.region} · {a.tags.join(' / ')}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {showReview && matchedReviews.length > 0 && (
              <section>
                <h2 className="mb-4 text-sm font-semibold text-foreground-400">
                  相关影评（{matchedReviews.length}）
                </h2>
                <div className="flex flex-col gap-3">
                  {matchedReviews.map((r) => (
                    <Link
                      key={r.id}
                      to={`/review/${r.id}`}
                      className="group flex gap-4 rounded-lg border border-background-800 bg-background-900/50 p-3 hover:border-primary-700/70 transition-colors cursor-pointer"
                    >
                      <div className="h-20 w-28 shrink-0 overflow-hidden rounded-md bg-background-800">
                        <img src={r.cover} alt={`${r.title} 封面`} title={`${r.title} - 成人短剧影评`} className="w-full h-full object-cover object-top" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-semibold text-foreground-50 group-hover:text-primary-300 transition-colors line-clamp-1">
                          {r.title}
                        </h3>
                        <p className="mt-1.5 text-xs text-foreground-400 line-clamp-2">{r.excerpt}</p>
                        <p className="mt-1 text-xs text-foreground-500">{r.author} · {r.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </SiteLayout>
  );
}